import { useState } from "react";

export default function VideoHistory({ videos = [], onRefresh }) {
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [downloading, setDownloading] = useState(null);

  if (!videos || videos.length === 0) {
    return null;
  }

  const completedVideos = videos.filter((v) => v.status === "completed" && v.video_url);

  if (completedVideos.length === 0) {
    return null;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDownload = async (videoUrl, index, videoId) => {
    setDownloading(videoId);
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `video-export-${index + 1}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(videoUrl, "_blank");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <span aria-hidden="true">📹</span>
          Exported Videos ({completedVideos.length})
        </h3>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            ↻ Refresh
          </button>
        )}
      </div>

      <div className="space-y-2">
        {completedVideos.map((video, index) => (
          <div
            key={video.id}
            className="border border-gray-100 rounded-lg overflow-hidden hover:border-gray-200 transition-colors"
          >
            <div
              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedVideo(expandedVideo === video.id ? null : video.id)}
            >
              <div className="w-16 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt={`Export ${completedVideos.length - index}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    🎬
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Export #{completedVideos.length - index}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDuration(video.duration)}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {formatDate(video.created_at)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(video.video_url, completedVideos.length - index - 1, video.id);
                  }}
                  disabled={downloading === video.id}
                  className="px-3 py-2 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors min-h-[44px] disabled:opacity-50"
                >
                  {downloading === video.id ? "Downloading..." : "⬇ Download"}
                </button>
                <span className="text-gray-300">
                  {expandedVideo === video.id ? "▲" : "▼"}
                </span>
              </div>
            </div>

            {expandedVideo === video.id && (
              <div className="border-t border-gray-100 p-3 bg-gray-50">
                <video
                  src={video.video_url}
                  controls
                  className="w-full rounded-lg max-h-64"
                  preload="metadata"
                />
                <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                  <span>
                    {video.resolution || "1080p"} • {video.aspect_ratio || "9:16"}
                  </span>
                  <a
                    href={video.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600"
                  >
                    Open in new tab ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-gray-400 text-center">
        Each export is saved separately. Re-exporting creates a new version.
      </p>
    </div>
  );
}
